/** Canonical Portal file audience shared with KIWAMI Portal. */
export type FileAudience = 'internal' | 'client' | 'subcontractor' | 'both';
export const DEFAULT_FILE_AUDIENCE: FileAudience = 'internal';
export const FILE_AUDIENCE_VALUES = ['internal', 'client', 'subcontractor', 'both'] as const;

export interface FileAudienceRecord {
  is_client_visible?: boolean | null;
  is_subcontractor_visible?: boolean | null;
  photo_type?: string | null;
}

export const CHANGE_ORDER_AUDIENCE_PHOTO_TYPE: Record<FileAudience, string> = {
  internal: 'co_vis_internal',
  client: 'co_vis_client',
  subcontractor: 'co_vis_subcontractor',
  both: 'co_vis_both',
};

export function parseFileAudience(value: unknown, fallback: FileAudience = DEFAULT_FILE_AUDIENCE): FileAudience {
  return value === 'internal' || value === 'client' || value === 'subcontractor' || value === 'both'
    ? value
    : fallback;
}

export function audienceToDbFlags(audience: FileAudience) {
  switch (audience) {
    case 'client': return { is_client_visible: true, is_subcontractor_visible: false };
    case 'subcontractor': return { is_client_visible: false, is_subcontractor_visible: true };
    case 'both': return { is_client_visible: true, is_subcontractor_visible: true };
    default: return { is_client_visible: false, is_subcontractor_visible: false };
  }
}

export function dbFlagsToAudience(record: FileAudienceRecord): FileAudience {
  const client = record.is_client_visible === true;
  const sub = record.is_subcontractor_visible === true;
  if (client && sub) return 'both';
  if (client) return 'client';
  if (sub) return 'subcontractor';
  return 'internal';
}

export function resolveAudienceFromRecord(record: FileAudienceRecord): FileAudience {
  if (record.is_client_visible != null || record.is_subcontractor_visible != null) return dbFlagsToAudience(record);
  switch (record.photo_type) {
    case 'co_vis_client': return 'client';
    case 'co_vis_subcontractor': return 'subcontractor';
    case 'co_vis_both': return 'both';
    default: return 'internal';
  }
}

export function buildFileAudienceWritePayload(audience: FileAudience, options?: { relatedType?: string | null; existingPhotoType?: string | null }) {
  const flags = audienceToDbFlags(audience);
  const isCo = options?.relatedType === 'change_order' || Boolean(options?.existingPhotoType?.startsWith('co_vis_'));
  return isCo ? { ...flags, photo_type: CHANGE_ORDER_AUDIENCE_PHOTO_TYPE[audience] } : flags;
}

export function getAudienceLabel(audience: FileAudience): string {
  if (audience === 'client') return 'Client';
  if (audience === 'subcontractor') return 'Subcontractor';
  if (audience === 'both') return 'Client + Subcontractor';
  return 'Internal only';
}

export function getAudienceBadgeClass(audience: FileAudience): string {
  if (audience === 'client') return 'bg-[#ECFDF5] text-[#059669]';
  if (audience === 'subcontractor') return 'bg-[#FEF2F2] text-[#E8621A]';
  if (audience === 'both') return 'bg-[#F5F3FF] text-[#7C3AED]';
  return 'bg-[#F3F4F6] text-[#6B7280]';
}
